/**
 * One operator press mails the whole list (#2616).
 *
 * A single `/send` is bounded by `EMAIL_NOTICE_SEND_BUDGET_MS` server-side,
 * which in practice is ~60 addresses, and the operator used to press again and
 * again to get through a list. The bound stays — it is what keeps "how many
 * went out" knowable — and the console now walks the list itself: the pending
 * account ids are cut into small `account_ids` batches and posted SERIALLY, so
 * two passes never overlap and every response is a progress tick.
 *
 * A batch that comes back with `deferred` rows is re-posted with only the rows
 * the server never touched (`status === 'pending'` in the response). Each
 * re-post strictly shrinks that set or stops, so the loop always ends. A row
 * that FAILED in this run is not retried by it — the delivery table's retry
 * buttons are the explicit way to do that.
 */
import type { EmailNoticeSendResponse } from '@nadoutong/contracts';

export const NOTICE_SEND_BATCH_SIZE = 10;

export interface NoticeSendProgress {
  readonly total: number;
  readonly done: number;
  readonly sent: number;
  readonly failed: number;
}

export async function runBatchedNoticeSend(input: {
  readonly accountIds: readonly string[];
  readonly sendBatch: (accountIds: readonly string[]) => Promise<EmailNoticeSendResponse>;
  readonly onProgress: (progress: NoticeSendProgress) => void;
}): Promise<NoticeSendProgress & { readonly deferred: number }> {
  const total = input.accountIds.length;
  let sent = 0;
  let failed = 0;
  let deferred = 0;
  input.onProgress({ total, done: 0, sent, failed });
  for (let start = 0; start < total; start += NOTICE_SEND_BATCH_SIZE) {
    let batch = input.accountIds.slice(start, start + NOTICE_SEND_BATCH_SIZE);
    while (batch.length > 0) {
      const result = await input.sendBatch(batch);
      sent += result.sent;
      failed += result.failed;
      if (result.deferred === 0) break;
      const untouched = new Set(
        result.recipients.filter((row) => row.status === 'pending').map((row) => row.account_id),
      );
      const leftover = batch.filter((id) => untouched.has(id));
      if (leftover.length >= batch.length) {
        // The server attempted nothing: re-posting would spin forever.
        deferred += result.deferred;
        break;
      }
      // A deferred row that was already `failed` is not `pending`, so it is not
      // re-posted — but it is still reported, never silently dropped.
      deferred += result.deferred - leftover.length;
      batch = leftover;
    }
    input.onProgress({ total, done: Math.min(total, start + NOTICE_SEND_BATCH_SIZE), sent, failed });
  }
  return { total, done: total, sent, failed, deferred };
}
